'use client';

import { useTranslation } from '@unifyapps/i18n/client';
import { getTranslatedObject } from '@unifyapps/i18n/utils/object';
import { useAggregate } from '@unifyapps/network/generated/aggregation-rest-api/aggregation-rest-api';
import { hashKey } from '@unifyapps/network/react-query';
import _keyBy from 'lodash/keyBy';
import _map from 'lodash/map';
import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { ModuleValues } from '../../consts/modules';
import { getAggregationQuery } from './query';
import useDefaultModuleDefs from './useDefaultModuleDefs';

type ModuleDefinition = {
  id: ModuleValues;
  name: string;
  pluralName: string;
};

export interface ModuleDefsContextType {
  moduleDefs: Record<ModuleValues, ModuleDefinition>;
}

const ModuleDefsProvider = createContext<ModuleDefsContextType | null>(null);

export const useModuleDefsContext = () => {
  const context = useContext(ModuleDefsProvider);

  if (!context) {
    throw new Error('useModuleDefsContext must be used within a ModuleDefsProvider');
  }

  return context;
};

export const ModuleDefsContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation(['module-defs']);

  const { data } = useAggregate(getAggregationQuery(), {
    query: {
      // In our default queryKeyhashFn, we add projectSlug in queryKey, but moduleDefs are project agnostic.
      queryKeyHashFn: hashKey,
      // Since navigation is global, we don't need to pass projectId so inside projects, the same navigation gets shown as in the global context
      meta: { disableProjectHeader: true },
    },
  });
  const defaultModuleDefs = useDefaultModuleDefs();
  const value = useMemo(() => {
    const fetchedModuleDefs = _keyBy(_map(data?.objects, 'columns'), 'id');
    const moduleDefs = { ...defaultModuleDefs, ...fetchedModuleDefs };
    return { moduleDefs };
  }, [data?.objects, defaultModuleDefs]) as ModuleDefsContextType;

  const translatedValue = useMemo(() => {
    return getTranslatedObject(value, t);
  }, [value, t]);

  return (
    <ModuleDefsProvider.Provider value={translatedValue}>{children}</ModuleDefsProvider.Provider>
  );
};

export function MatrixModuleDefsProvider({ children }: { children: React.ReactNode }) {
  const defaultModuleDefs = useDefaultModuleDefs();

  const value = useMemo(
    () => ({ moduleDefs: defaultModuleDefs }) as ModuleDefsContextType,
    [defaultModuleDefs],
  );

  return <ModuleDefsProvider.Provider value={value}>{children}</ModuleDefsProvider.Provider>;
}
